import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Upload, X, Clock, Camera, FileText } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useAppStore } from '@/lib/data';
import { useAuth } from '@/hooks/use-auth';
import { StreakService, ACTIVITY_TYPES } from '@/lib/streak-service';
import type { SavingsGroup, GroupContribution } from '@/lib/types';

const contributionSchema = z.object({
  amount: z.number().min(1, 'Amount must be at least 1'),
  method: z.enum(['cash', 'bank_transfer', 'mobile_money', 'other']),
  description: z.string().optional(),
});

type ContributionFormValues = z.infer<typeof contributionSchema>;

interface ContributeModalProps {
  group: SavingsGroup;
  children?: React.ReactNode;
  trigger?: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ContributeModal({
  group,
  children,
  trigger,
  isOpen: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: ContributeModalProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { addGroupContribution, updateSavingsGroup } = useAppStore();

  const [isInternalOpen, setIsInternalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen! : isInternalOpen;
  const setIsOpen = isControlled ? controlledOnOpenChange! : setIsInternalOpen;

  const form = useForm<ContributionFormValues>({
    resolver: zodResolver(contributionSchema),
    defaultValues: {
      amount: group.contributionRules?.minAmount || 1,
      method: 'mobile_money',
      description: '',
    },
  });

  const progressPercentage = group.targetAmount > 0
    ? (group.currentAmount / group.targetAmount) * 100
    : 0;

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Max file size is 5MB.', variant: 'destructive' });
      return;
    }
    setUploadedFile(file);
    if (file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }
  };

  const removeFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setUploadedFile(null);
    setPreviewUrl(null);
  };

  const onSubmit = async (values: ContributionFormValues) => {
    setIsSubmitting(true);
    try {
      const contribution: GroupContribution = {
        id: `contrib-${Date.now()}`,
        groupId: group.id,
        memberId: user?.id || 'current-user',
        amount: values.amount,
        description: values.description,
        contributedAt: new Date().toISOString(),
        method: values.method,
        status: 'pending',
        proofFile: uploadedFile?.name,
      };

      await addGroupContribution(contribution);

      if (user?.id) {
        StreakService.recordActivity(user.id, ACTIVITY_TYPES.SAVINGS_CONTRIBUTION);
      }

      toast({
        title: 'Contribution submitted!',
        description: `${formatCurrency(values.amount)} submitted for review.`,
      });

      form.reset();
      removeFile();
      setIsOpen(false);
    } catch {
      toast({ title: 'Error submitting contribution', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const paymentMethods = [
    { value: 'mobile_money', label: 'Mobile Money', icon: '📱' },
    { value: 'bank_transfer', label: 'Bank Transfer', icon: '🏦' },
    { value: 'cash', label: 'Cash Deposit', icon: '💵' },
    { value: 'other', label: 'Other', icon: '💳' },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children || trigger || <Button><Upload className="h-3 w-3 mr-2" />Contribute</Button>}
      </DialogTrigger>

      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Contribute to {group.name}
          </DialogTitle>
          <DialogDescription>
            Submit your contribution for admin verification.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Progress */}
          <Card className="bg-muted/30">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Group Progress</span>
                <Badge variant="secondary">{progressPercentage.toFixed(1)}%</Badge>
              </div>
              <Progress value={progressPercentage} className="h-2" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{formatCurrency(group.currentAmount)}</span>
                <span>{formatCurrency(group.targetAmount)}</span>
              </div>
            </CardContent>
          </Card>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Amount */}
            <div className="space-y-1.5">
              <Label>Amount</Label>
              <Input
                type="number"
                step="0.01"
                min={group.contributionRules?.minAmount || 1}
                placeholder="0.00"
                {...form.register('amount', { valueAsNumber: true })}
              />
              {form.formState.errors.amount && (
                <p className="text-xs text-destructive">{form.formState.errors.amount.message}</p>
              )}
            </div>

            {/* Method */}
            <div className="space-y-1.5">
              <Label>Payment Method</Label>
              <Select value={form.watch('method')} onValueChange={(v) => form.setValue('method', v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {paymentMethods.map(m => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.icon} {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label>Note <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea rows={2} placeholder="Any notes..." {...form.register('description')} />
            </div>

            {/* Proof upload */}
            <div className="space-y-1.5">
              <Label>Proof of Payment <span className="text-muted-foreground font-normal">(optional)</span></Label>
              {!uploadedFile ? (
                <label htmlFor="proof-upload" className="cursor-pointer flex flex-col items-center gap-2 border-2 border-dashed border-muted-foreground/25 rounded-lg p-5 text-center hover:border-primary/40 transition-colors">
                  <Camera className="h-7 w-7 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Click to upload image or PDF (max 5MB)</p>
                  <input id="proof-upload" type="file" accept="image/*,.pdf" onChange={handleFileUpload} className="hidden" />
                </label>
              ) : (
                <div className="flex items-center gap-3 border rounded-lg p-3">
                  {previewUrl
                    ? <img src={previewUrl} alt="preview" className="h-12 w-12 object-cover rounded" />
                    : <FileText className="h-10 w-10 text-muted-foreground" />
                  }
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{uploadedFile.name}</p>
                    <p className="text-xs text-muted-foreground">{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={removeFile}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Notice */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 text-blue-800 text-xs">
              <Clock className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Your contribution will be reviewed by the group admin before being counted.</span>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)} className="flex-1" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Contribution'}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
